import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BookOpen, Users, Award, Activity, ArrowUpRight } from "lucide-react";

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="border border-border p-6 card-lift bg-background">
      <div className="flex items-start justify-between">
        <div className="eyebrow">{label}</div>
        <Icon size={16} strokeWidth={1.5} className="text-muted-foreground" />
      </div>
      <div className="mt-6 text-4xl font-black tracking-tighter">{value}</div>
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-6 md:p-12 max-w-7xl">
      <header className="mb-12">
        <div className="eyebrow mb-3">/ Overview</div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Dashboard</h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-xl">
          A snapshot of your courses and candidate progress. Build a course, generate a link, and watch certificates flow.
        </p>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <StatCard testid="stat-courses" label="/ Courses" value={stats?.courses ?? "—"} icon={BookOpen} />
        <StatCard testid="stat-candidates" label="/ Candidates" value={stats?.candidates ?? "—"} icon={Users} />
        <StatCard testid="stat-completed" label="/ Completed" value={stats?.completed ?? "—"} icon={Award} />
        <StatCard testid="stat-completion-rate" label="/ Completion rate" value={stats ? `${stats.completion_rate}%` : "—"} icon={Activity} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Link to="/admin/courses/new" data-testid="quick-new-course" className="border border-border p-8 card-lift block group">
          <div className="flex items-center justify-between mb-6">
            <div className="eyebrow">/ Quick action</div>
            <ArrowUpRight size={18} strokeWidth={1.5} className="opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <h3 className="text-2xl font-semibold tracking-tight">Build a new course</h3>
          <p className="mt-3 text-sm text-muted-foreground">Add modules (video, text, PDF) and a quiz. Set your CPD hours and pass rate.</p>
        </Link>
        <Link to="/admin/candidates" data-testid="quick-new-candidate" className="border border-border p-8 card-lift block group">
          <div className="flex items-center justify-between mb-6">
            <div className="eyebrow">/ Quick action</div>
            <ArrowUpRight size={18} strokeWidth={1.5} className="opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <h3 className="text-2xl font-semibold tracking-tight">Invite a candidate</h3>
          <p className="mt-3 text-sm text-muted-foreground">Enter their name & email, pick a course, copy the magic link and send it.</p>
        </Link>
      </section>

      <section className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="border border-border p-5">
          <div className="eyebrow">/ Pending</div>
          <div className="mt-3 text-2xl font-bold">{stats?.pending ?? "—"}</div>
        </div>
        <div className="border border-border p-5">
          <div className="eyebrow">/ In progress</div>
          <div className="mt-3 text-2xl font-bold">{stats?.in_progress ?? "—"}</div>
        </div>
        <div className="border border-border p-5">
          <div className="eyebrow">/ Completed</div>
          <div className="mt-3 text-2xl font-bold">{stats?.completed ?? "—"}</div>
        </div>
        <div className="border border-border p-5">
          <div className="eyebrow">/ Quiz failed</div>
          <div className="mt-3 text-2xl font-bold">{stats?.quiz_failed ?? "—"}</div>
        </div>
      </section>
    </div>
  );
}
